import { Div, Icon, Label, Loader, Touchable } from "@grassrootly/rn-core";
import { ScreenLayout } from "../../layout/components";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { useHome } from './useHome';
import NextGame from '../../components/game/next-game/NextGame';
import { ScrollView } from 'react-native-gesture-handler';
import { ScrollView as RNScrollView, Image, ImageBackground, View } from 'react-native';
import { images } from "../../layout/assets";
import { PlayerRating, PlayerWinner } from "../../components/shared";
import moment from "moment";
import GamesList from '../../components/game/games-list/GamesList';
import NonGroupMemberCTA from '../../components/game/ctas/NonGroupMemberCTA';
import CreateNewGameCTA from '../../components/game/ctas/CreateNewGameCTA';

// ToDo [up]: This will be removed, used for testing puropses at the moment
const ONLY_ADMIN_CAN_CREATE_GAME = !process.env.EXPO_PUBLIC_NON_ADMIN_CAN_CREATE_GAME;

export default function HomeScreenRM({ route, navigation }: MainStackScreenProps<"home_remastered">) {
  const {
    previousGames,
    loadingPreviousGames,
    loadMorePreviousGames,
    loadingMorePreviousGames,
    hasMorePreviousGames,
    setGameAttendancy,
    updateAttendancyInProgress,
    groups,
    isMemberOfAnyGroup,
    isAdminOfAnyGroup,
    openGameScreen,
    featuredGame,
    loadingFeaturedGame,
    latestMatches,
    playerMonthlyForm,
    loadingPlayerMonthlyForm,
  } = useHome({ route, navigation });

  return (
    <ScreenLayout
      header={{
        center: `HOME`,
        left: <Icon name="menu" size={24} onPress={navigation.toggleDrawer} classNames={["mb-{5}"]} />,
        right: isAdminOfAnyGroup ? (
          <Icon 
            name="add" 
            size={28} 
            onPress={() => navigation.navigate("new-game", {})} 
            classNames={["mb-{5}"]} 
          />
        ) : undefined,
      }}
    >
      <ScrollView
        style={{ marginTop: -16, flex: 1 }}
        contentContainerStyle={{
          paddingTop: 24,
          paddingBottom: 20,
        }}
        showsVerticalScrollIndicator={false}>

        {/* Show loader while loading feature game */}
        {loadingFeaturedGame && <Div classNames={['mt-{20}']}><Loader size={32}></Loader></Div>}

        {/* 0. If Player has a featured game (either as group member or added to specific game) */}
        {!!featuredGame && (
          <Div>
            {/* Section headline */}
            <Div classNames={["pb-{12}", "alignSelfFlexStart"]}>
              <Div classNames={["flexRow", "alignItemsCenter", "gap8", "bgNeutralDarkerDark", "rounded", "ph12", "pv4"]}>
                <Icon name="sports_soccer" size={18} />
                <Label type="l3Bold">{moment(featuredGame.game_datetime).isAfter(moment()) ? "Your next match" : "Your featured match"}</Label>
              </Div>
            </Div>
            <NextGame
              game={featuredGame}
              navigation={navigation}
              setGameAttendancy={setGameAttendancy}
              updateAttendancyInProgress={updateAttendancyInProgress}
            />
          </Div>
        )}

        {/* 1. If Player is not member of any group and has no featured game, show him possibility to create/or join a group */}
        {!isMemberOfAnyGroup && !loadingFeaturedGame && !featuredGame && <NonGroupMemberCTA navigation={navigation} />}

        {/* 2. If Player is a non-admin member of any group show him possibility to create/or join a group */}
        {isMemberOfAnyGroup && !isAdminOfAnyGroup && !loadingFeaturedGame && !featuredGame && <NonGroupMemberCTA navigation={navigation} />}

        {/* 3. If Player is an admin of any group and there is not upcoming game show him possibility to create new game */}
        {isAdminOfAnyGroup && !!groups && !loadingFeaturedGame && !featuredGame && <CreateNewGameCTA navigation={navigation} groups={groups.filter((x) => !ONLY_ADMIN_CAN_CREATE_GAME || x.is_owner)} />}

        {/* Latest matches on eqipa - horizontal card carousel */}
        {latestMatches.length > 0 && (
          <Div classNames={["mt-{30}"]}>
            {/* Section headline */}
            <Div classNames={["pb-{12}", "alignSelfFlexStart"]}>
              <Div classNames={["flexRow", "alignItemsCenter", "gap8", "bgNeutralDarkerDark", "rounded", "ph12", "pv4"]}>
                <Icon name="schedule" size={18} />
                <Label type="l3Bold">Recent matches</Label>
              </Div>
            </Div>

            {/* Horizontal cards */}
            <RNScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ paddingHorizontal: 4 }}
            >
              {latestMatches.map((match) => (
                <Touchable
                  key={match.game_id}
                  onPress={() => openGameScreen(match.game_id)}
                  classNames={["mr-{12}", "bw-{1}", "borderSecondary", "rounded", "overflowHidden", "minW-{260}"]}
                >
                  <View style={{ width: "100%", minHeight: 180, borderRadius: 8 }}>
                    <Div classNames={["bgPrimaryWashed", "p12", "flexColumn", "justifyContentSpaceBetween", "h-{100%}"]}>
                      {/* Header: prefers organization/season info, falls back to group info (mirrors games_view semantics) */}
                      <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "gap8", "mb-{8}", "pt-{8}"]}>
                        <Image
                          source={
                            match.organization_logo
                              ? { uri: match.organization_logo }
                              : match.group_logo
                              ? { uri: match.group_logo }
                              : images.groupAvatar
                          }
                          style={{ width: 24, height: 24, borderRadius: 12 }}
                          resizeMode="cover"
                        />
                        <Label numberOfLines={1} ellipsizeMode="tail" type="l3Bold" classNames={["textCenter"]}>
                          {match.season_summary || match.group_name}
                        </Label>
                      </Div>

                      {/* Teams and score - centered vertically between header and date */}
                      <Div classNames={["flex1", "justifyContentCenter"]}>
                        <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "gap16", "mb-{8}"]}>
                          {/* Host team */}
                          <Div classNames={["flexColumn", "alignItemsCenter", "flex1", "justifyContentCenter"]}>
                            <Image
                              source={
                                match.host_team_logo
                                  ? { uri: match.host_team_logo }
                                  : images.whiteTeamLogo
                              }
                              style={{ height: 45, width: 45, borderRadius: 45 }}
                              resizeMode="cover"
                            />
                            <Label
                              numberOfLines={1}
                              ellipsizeMode="tail"
                              type="l3Bold"
                              classNames={["mt-{8}", "textCenter"]}
                            >
                              {match.game_host_team_name}
                            </Label>
                          </Div>

                          {/* Score */}
                          <Div classNames={["flexColumn", "alignItemsCenter", "justifyContentCenter"]}>
                            <Label type="l2Bold" classNames={["fontSize24"]}>
                              {(match.game_host_score ?? 0)} - {(match.game_visitor_score ?? 0)}
                            </Label>
                          </Div>

                          {/* Visitor team */}
                          <Div classNames={["flexColumn", "alignItemsCenter", "flex1", "justifyContentCenter"]}>
                            <Image
                              source={
                                match.visitor_team_logo
                                  ? { uri: match.visitor_team_logo }
                                  : images.blackTeamLogo
                              }
                              style={{ height: 45, width: 45, borderRadius: 45 }}
                              resizeMode="cover"
                            />
                            <Label
                              numberOfLines={1}
                              ellipsizeMode="tail"
                              type="l3Bold"
                              classNames={["mt-{8}", "textCenter"]}
                            >
                              {match.game_visitor_team_name}
                            </Label>
                          </Div>
                        </Div>
                      </Div>

                      {/* Date & time at bottom of card with extra bottom padding */}
                      <Div classNames={["flexRow", "alignItemsCenter", "justifyContentCenter", "gap8", "pb-{8}"]}>
                        <Label numberOfLines={1} type="l3Bold">
                          {moment(match.game_datetime).format("DD.MM.YY HH:mm")}
                        </Label>
                        {match.game_type === 'TRAINING' && (
                          <Div classNames={["bgSecondaryLight", "rounded"]}>
                            <Label classNames={["ph8", "pv2"]} type="l4Regular">
                              Training
                            </Label>
                          </Div>
                        )}
                      </Div>
                    </Div>
                  </View>
                </Touchable>
              ))}
            </RNScrollView>
          </Div>
        )}

        {/* Your form - last 3 full months of personal stats (table layout) */}
        {playerMonthlyForm.length > 0 && (
          <Div classNames={["mt-{30}"]}>
            {/* Section headline */}
            <Div classNames={["pb-{12}", "alignSelfFlexStart"]}>
              <Div classNames={["flexRow", "alignItemsCenter", "gap8", "bgNeutralDarkerDark", "rounded", "ph12", "pv4"]}>
                <Icon name="analytics" size={18} />
                <Label type="l3Bold">Your form</Label>
              </Div>
            </Div>

            <Div classNames={["bw-{1}", "borderSecondary", "rounded", "ph12", "pv8"]}>
              {/* Header row: months */}
              <Div classNames={["flexRow", "mb-{8}"]}>
                <Div classNames={["flex1", "pl-{4}"]}>
                  {/* empty top-left cell for row labels */}
                </Div>
                {playerMonthlyForm.map((row) => (
                  <Div key={row.month_key} classNames={["flex1", "alignItemsCenter"]}>
                    <Label
                      type="l4Bold"
                      classNames={["textCenter", "textUppercase", "borderBottomSolid", "bwBottom-{1}", "pb-{4}", "colorPrimary"]}
                    >
                      {row.month_label}
                    </Label>
                  </Div>
                ))}
              </Div>

              {/* Helper to render one stat row */}
              {[
                { key: "matches", label: "Matches", format: (v: number) => v.toString() },
                { key: "avg_rating", label: "Avg rating", format: (v: number) => v.toFixed(1) },
                { key: "goals", label: "Goals", format: (v: number) => v.toString() },
                { key: "assists", label: "Assists", format: (v: number) => v.toString() },
                { key: "wins", label: "Wins", format: (v: number) => v.toString() },
              ].map((rowDef) => (
                <Div key={rowDef.key} classNames={["flexRow", "alignItemsCenter", "mb-{6}"]}>
                  <Div classNames={["flex1", "pl-{4}"]}>
                    {rowDef.key === "goals" ? (
                      <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                        <Icon name="sports_soccer" size={18} />
                      </Div>
                    ) : rowDef.key === "assists" ? (
                      <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                        <Image source={images.assist} style={{ height: 14, width: 24 }} />
                      </Div>
                    ) : rowDef.key === "wins" ? (
                      <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                        <PlayerWinner
                          outcome="W"
                          playerAttended={true}
                          size={14}
                          classNames={["h-{20}", "w-{20}"]}
                        />
                      </Div>
                    ) : (
                      <Label type="l4Regular">{rowDef.label}</Label>
                    )}
                  </Div>
                  {playerMonthlyForm.map((month, monthIndex) => {
                    const currentRaw = ((month as any)[rowDef.key] ?? 0) as number;
                    const hasGames = (month as any).matches > 0;
                    const displayValue =
                      rowDef.key === "matches"
                        ? rowDef.format(currentRaw)
                        : hasGames
                        ? rowDef.format(currentRaw)
                        : "-";

                    const prevRaw =
                      monthIndex > 0
                        ? (((playerMonthlyForm[monthIndex - 1] as any)[rowDef.key] ?? 0) as number)
                        : null;

                    const showUp = prevRaw !== null && currentRaw > prevRaw;
                    const showDown = prevRaw !== null && currentRaw < prevRaw;

                    return (
                      <Div
                        key={month.month_key + rowDef.key}
                        classNames={["flex1", "alignItemsCenter"]}
                      >
                        <Div
                          classNames={[
                            "flexRow",
                            "alignItemsCenter",
                            "justifyContentCenter",
                            "gap4",
                          ]}
                        >
                          {rowDef.key === "avg_rating" ? (
                            hasGames && currentRaw > 0 ? (
                              <PlayerRating
                                rating={currentRaw}
                                decimalPlaces={1}
                                classNames={["p-{4}"]}
                              />
                            ) : (
                              <Label type="l3Bold" classNames={["textCenter"]}>
                                -
                              </Label>
                            )
                          ) : (
                            <Label type="l3Bold" classNames={["textCenter"]}>
                              {displayValue}
                            </Label>
                          )}
                          {(() => {
                            const arrowName = showUp
                              ? "arrow_drop_up"
                              : showDown
                              ? "arrow_drop_down"
                              : "arrow_drop_up";
                            const arrowColor = showUp
                              ? undefined
                              : showDown
                              ? undefined
                              : "transparent";
                            const arrowClassNames = showUp
                              ? ["textSuccess"]
                              : showDown
                              ? ["textDanger"]
                              : [];

                            return (
                              <Icon
                                name={arrowName}
                                size={16}
                                color={arrowColor}
                                classNames={arrowClassNames}
                              />
                            );
                          })()}
                        </Div>
                      </Div>
                    );
                  })}
                </Div>
              ))}
            </Div>
          </Div>
        )}
      </ScrollView>
    </ScreenLayout>
  );
}
